import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SlideModal from '../components/slideModal';
import { LuUser, LuUsers, LuCircleAlert, LuPaperclip, LuInfo } from 'react-icons/lu';
import { MdChevronLeft, MdClose } from 'react-icons/md';
import type { CaseFormData, FeeBreakdown } from './types';

const DEFAULT_FEES: FeeBreakdown = { items: [{ label: 'Service Fee', amount: 10000 }], totalFee: 10000 };
const formatNaira = (n: number) => `₦${Number(n || 0).toLocaleString()}`;

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-[#eee] p-5">
            <div className="mb-4 flex items-center gap-2 border-b border-[#eee] pb-3">
                {icon}
                <h3 className="text-[15px] font-bold text-secondary">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0 pr-2">
            <p className="text-[12px] font-semibold text-secondary">{label}</p>
            <p className="mt-1 text-[13px] break-words text-[#555]">{value || 'N/A'}</p>
        </div>
    );
}

/* ─── Payment Modal ─── */

function PaymentModal({
    open,
    onClose,
    caseType,
    formData,
    fees,
    hidePaymentMethod,
    onPaymentMethodChange,
    onSubmit,
    onSuccess,
}: {
    open: boolean;
    onClose: () => void;
    caseType: string;
    formData: CaseFormData;
    fees?: FeeBreakdown;
    hidePaymentMethod?: boolean;
    onPaymentMethodChange: (m: 'Cash' | 'POS' | 'Transfer') => void;
    onSubmit: (caseType: string, formData: CaseFormData) => Promise<void>;
    onSuccess?: () => void;
}) {
    const breakdown = fees ?? DEFAULT_FEES;
    const [view, setView] = useState<'form' | 'success'>('form');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setView('form');
            setError('');
        }
    }, [open]);

    const handleClose = () => {
        onClose();
        if (view === 'success') onSuccess?.();
    };

    const handleConfirm = async () => {
        setLoading(true);
        setError('');
        try {
            await onSubmit(caseType, formData);
            setView('success');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create case.');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-[70] bg-black/40" onClick={handleClose} />
            <div className="fixed inset-0 z-[71] flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                    {view === 'form' ? (
                        <div className="flex flex-col">
                            <div className="flex items-center justify-between px-6 pt-6 pb-4">
                                <div className="flex items-center gap-3">
                                    {/* <button type="button" title="Back" onClick={handleClose} className="flex h-8 w-8 items-center justify-center rounded-full text-[#555] hover:bg-[#f5f5f5]"><MdChevronLeft size={20} /></button> */}
                                    <h2 className="text-lg font-bold text-secondary">Payment Details</h2>
                                </div>
                                <button
                                    type="button"
                                    title="Close"
                                    onClick={handleClose}
                                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#555] hover:bg-[#f5f5f5]"
                                >
                                    <MdClose size={20} />
                                </button>
                            </div>
                            <div className="mx-6 rounded-lg border border-[#eee]">
                                <div className="border-b border-[#eee] px-4 py-3">
                                    <p className="text-[14px] font-bold text-secondary">Fee Breakdown</p>
                                </div>
                                {breakdown.items.length === 0 ? (
                                    <div className="px-4 py-2.5 text-[13px] text-[#888]">No fees configured</div>
                                ) : (
                                    breakdown.items.map((it, i) => (
                                        <div key={i} className="flex items-center justify-between px-4 py-2.5">
                                            <span className="text-[13px] text-[#888]">{it.label}</span>
                                            <span className="text-[13px] font-medium text-secondary">
                                                {formatNaira(it.amount)}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="mx-6 mt-4 rounded-lg bg-[#f5f5f5] px-4 py-3">
                                <p className="text-[12px] text-[#888]">Total Fee</p>
                                <p className="mt-1 text-2xl font-bold text-secondary">
                                    {formatNaira(breakdown.totalFee)}
                                </p>
                            </div>
                            {!hidePaymentMethod && (
                                <div className="mx-6 mt-4 rounded-lg border border-[#eee] p-4">
                                    <p className="mb-3 text-[14px] font-bold text-secondary">Payment Method</p>
                                    {(['Cash', 'POS', 'Transfer'] as const).map((m) => (
                                        <label
                                            key={m}
                                            className="flex cursor-pointer items-center gap-3 py-2 text-[14px] text-secondary"
                                        >
                                            <input
                                                type="radio"
                                                name="pay"
                                                checked={(formData.paymentMethod || 'Cash') === m}
                                                onChange={() => onPaymentMethodChange(m)}
                                                className="h-4 w-4 accent-primary"
                                            />
                                            {m}
                                        </label>
                                    ))}
                                </div>
                            )}
                            {error && (
                                <div className="mx-6 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600">
                                    {error}
                                </div>
                            )}
                            <div className="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    onClick={handleConfirm}
                                    disabled={loading}
                                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[14px] font-bold text-white hover:bg-secondary-400 disabled:opacity-50"
                                >
                                    {loading ? 'Processing...' : 'Confirm Payment'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="relative flex flex-col items-center px-8 py-12 text-center">
                            <button
                                type="button"
                                title="Close"
                                onClick={handleClose}
                                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#555] hover:bg-[#f5f5f5]"
                            >
                                <MdClose size={20} />
                            </button>
                            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                                <circle cx="40" cy="40" r="36" fill="url(#pg)" />
                                {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
                                    <circle
                                        key={a}
                                        cx={40 + 30 * Math.cos((a * Math.PI) / 180)}
                                        cy={40 + 30 * Math.sin((a * Math.PI) / 180)}
                                        r="10"
                                        fill="#22c55e"
                                    />
                                ))}
                                <circle cx="40" cy="40" r="24" fill="#22c55e" />
                                <path
                                    d="M28 40l8 8 16-16"
                                    stroke="white"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                />
                                <defs>
                                    <radialGradient id="pg" cx=".5" cy=".3" r=".7">
                                        <stop offset="0%" stopColor="#4ade80" />
                                        <stop offset="100%" stopColor="#16a34a" />
                                    </radialGradient>
                                </defs>
                            </svg>
                            <h2 className="mt-6 text-xl font-bold text-secondary">Case created successfully!</h2>
                            <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-[#888]">
                                The case has been recorded and is now available in the dashboard.
                            </p>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="mt-8 flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[14px] font-bold text-white hover:bg-secondary-400"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
}

/* ─── Review Content ─── */

function WalkInReview({ formData }: { formData: CaseFormData }) {
    return (
        <div className="space-y-5">
            <SectionCard icon={<LuUser size={18} className="text-secondary" />} title="Applicants">
                {(formData.applicants || []).map((p, i) => (
                    <div
                        key={i}
                        className={`grid grid-cols-2 gap-y-4 sm:grid-cols-3 ${i > 0 ? 'mt-6 border-t border-[#f0f0f0] pt-4' : ''}`}
                    >
                        <Field label="Name" value={p.name} />
                        <Field label="Type" value={formData.applicantType || ''} />
                        <Field label="Email" value={p.email || ''} />
                        <Field label="Phone" value={p.phone || ''} />
                        <Field label="Address" value={p.address || ''} />
                    </div>
                ))}
            </SectionCard>
            <SectionCard icon={<LuUsers size={18} className="text-secondary" />} title="Respondents">
                {(formData.respondents || []).map((p, i) => (
                    <div
                        key={i}
                        className={`grid grid-cols-2 gap-y-4 sm:grid-cols-3 ${i > 0 ? 'mt-6 border-t border-[#f0f0f0] pt-4' : ''}`}
                    >
                        <Field label="Name" value={p.name} />
                        <Field label="Type" value={formData.respondentType || ''} />
                        <Field label="Email" value={p.email || ''} />
                        <Field label="Phone" value={p.phone || ''} />
                        <Field label="Address" value={p.address || ''} />
                    </div>
                ))}
            </SectionCard>
            <SectionCard icon={<LuCircleAlert size={18} className="text-secondary" />} title="Dispute">
                <div className="grid grid-cols-2 gap-y-4">
                    <Field label="Category" value={formData.disputeCategory} />
                </div>
                <div className="mt-4">
                    <p className="text-[12px] font-semibold text-secondary">Description</p>
                    <p className="mt-1 text-[13px] leading-relaxed text-[#555]">
                        {formData.description || 'No description'}
                    </p>
                </div>
            </SectionCard>
            <SectionCard icon={<LuPaperclip size={18} className="text-secondary" />} title="Evidence">
                <div className="flex flex-wrap gap-3">
                    {formData.files.map((f, i) => (
                        <span key={i} className="rounded-md bg-[#f5f5f5] px-3 py-1.5 text-[12px] text-[#555]">
                            {f.name}
                        </span>
                    ))}
                    {formData.files.length === 0 && <p className="text-[13px] text-[#888]">No files</p>}
                </div>
            </SectionCard>
        </div>
    );
}

function CourtReferralReview({ formData }: { formData: CaseFormData }) {
    return (
        <div className="space-y-5">
            <SectionCard icon={<LuInfo size={18} className="text-secondary" />} title="General">
                <div className="grid grid-cols-3 gap-y-4">
                    <Field
                        label="Filing Date"
                        value={formData.filingDate ? new Date(formData.filingDate).toLocaleDateString() : ''}
                    />
                    <Field label="Type" value={formData.caseType || ''} />
                    <Field label="Suit Number" value={formData.suitNumber || ''} />
                </div>
            </SectionCard>
            <SectionCard icon={<LuUser size={18} className="text-secondary" />} title="User/Lawyer">
                <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-3">
                    <Field label="Name" value={formData.lawyerName || ''} />
                    <Field label="Firm" value={formData.lawFirm || ''} />
                    <Field label="Phone" value={formData.lawyerPhone || ''} />
                    <Field label="Email" value={formData.lawyerEmail || ''} />
                </div>
            </SectionCard>
            <SectionCard icon={<LuUsers size={18} className="text-secondary" />} title="Defendant">
                <div className="grid grid-cols-3 gap-y-4">
                    <Field label="Name" value={formData.defendant?.name || ''} />
                    <Field label="Address" value={formData.defendant?.address || ''} />
                    <Field label="Phone" value={formData.defendant?.phone || ''} />
                </div>
            </SectionCard>
            <SectionCard icon={<LuUser size={18} className="text-secondary" />} title="Claimant">
                <div className="grid grid-cols-3 gap-y-4">
                    <Field label="Name" value={formData.claimant?.name || ''} />
                    <Field label="Address" value={formData.claimant?.address || ''} />
                    <Field label="Phone" value={formData.claimant?.phone || ''} />
                </div>
            </SectionCard>
            <SectionCard icon={<LuPaperclip size={18} className="text-secondary" />} title="Evidence">
                {(() => {
                    const docNames = Array.from(formData.uploadedDocs || []);
                    const pairs = docNames.map((doc, i) => ({ doc, file: formData.files[i]?.name ?? '—' }));
                    const extras = formData.files.slice(docNames.length);
                    if (pairs.length === 0 && extras.length === 0) {
                        return <p className="text-[13px] text-[#888]">No evidence uploaded</p>;
                    }
                    return (
                        <div className="space-y-3">
                            {pairs.map(({ doc, file }, i) => (
                                <div key={i} className="flex items-baseline gap-2 text-[13px]">
                                    <span className="font-semibold text-secondary">{doc}:</span>
                                    <span className="text-[#555] break-all">{file}</span>
                                </div>
                            ))}
                            {extras.length > 0 && (
                                <div className="mt-2">
                                    <p className="mb-2 text-[12px] font-semibold text-secondary">Additional files</p>
                                    <div className="flex flex-wrap gap-2">
                                        {extras.map((f, i) => (
                                            <span
                                                key={i}
                                                className="rounded-md bg-[#f5f5f5] px-3 py-1.5 text-[12px] text-[#555]"
                                            >
                                                {f.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </SectionCard>
        </div>
    );
}

/* ─── Main Review Modal ─── */

export interface CaseReviewModalProps {
    open: boolean;
    onClose: () => void;
    caseType: string;
    formData: CaseFormData;
    fees?: FeeBreakdown;
    /** Hide the Cash / POS / Transfer radio — used when payment is handled
     *  off-site (e.g. a gateway redirect). */
    hidePaymentMethod?: boolean;
    onUpdate: (u: Partial<CaseFormData>) => void;
    onSubmit: (caseType: string, formData: CaseFormData) => Promise<void>;
    onSuccess?: () => void;
}

export default function CaseReviewModal({
    open,
    onClose,
    caseType,
    formData,
    fees,
    hidePaymentMethod,
    onUpdate,
    onSubmit,
    onSuccess,
}: CaseReviewModalProps) {
    const [paymentOpen, setPaymentOpen] = useState(false);

    return (
        <>
            <SlideModal open={open} onClose={onClose} title="Case Details" maxWidth="max-w-2xl">
                <div className="flex-1 overflow-y-auto p-6">
                    {caseType === 'court-referral' ? (
                        <CourtReferralReview formData={formData} />
                    ) : (
                        <WalkInReview formData={formData} />
                    )}
                </div>
                <div className="flex items-center gap-4 px-6 pb-6 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex flex-1 items-center justify-center rounded-xl border border-[#eee] py-4 text-[14px] font-semibold text-secondary hover:bg-[#f5f5f5]"
                    >
                        Edit Case Details
                    </button>
                    <button
                        type="button"
                        onClick={() => setPaymentOpen(true)}
                        className="flex flex-1 items-center justify-center rounded-xl bg-secondary py-4 text-[14px] font-bold text-white hover:bg-secondary-400"
                    >
                        Confirm Payment
                    </button>
                </div>
            </SlideModal>
            <PaymentModal
                open={paymentOpen}
                onClose={() => setPaymentOpen(false)}
                caseType={caseType}
                formData={formData}
                fees={fees}
                hidePaymentMethod={hidePaymentMethod}
                onPaymentMethodChange={(m) => onUpdate({ paymentMethod: m })}
                onSubmit={onSubmit}
                onSuccess={onSuccess}
            />
        </>
    );
}
